import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@design-system';
import { CurrencyInput, IntegerInput, PercentInput } from '@/shared/components/numeric-input';

import { useCcrcTypeAManualForm } from '@/features/ccrc-type-a/hooks/use-ccrc-type-a-manual-form';

import { type ReactNode } from 'react';

interface CcrcTypeAManualSheetProps {
  clientId: string;
  open: boolean;
  onClose: () => void;
  fundingSourceId?: string;
}

const SUBMISSION_DISCLAIMER =
  "By submitting, you acknowledge that Waterlily calculates the projected LTC coverage of each policy independently. If multiple policies are added, each is assessed as if it were the sole provider of coverage. No sequencing or priority is assumed, meaning one policy does not pay first before another covers the remaining costs. As a result, LTC coverage could be overestimated if multiple policy's benefits are applied to the same projected costs.";

interface FieldRowProps {
  title: string;
  description: string;
  children: ReactNode;
}

function FieldRow({ title, description, children }: FieldRowProps) {
  return (
    <div className="flex flex-col gap-3 py-4">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-foreground text-lg">{title}</h3>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      <div className="w-full">{children}</div>
    </div>
  );
}

export function CcrcTypeAManualSheet({
  clientId,
  open,
  onClose,
  fundingSourceId,
}: CcrcTypeAManualSheetProps) {
  const { formState, errors, handleNameChange, handleFieldChange, handleSubmit } =
    useCcrcTypeAManualForm({ clientId, open, onClose, fundingSourceId });

  return (
    <Sheet
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>
            {fundingSourceId
              ? 'Edit Life Planning Community Product'
              : 'Add a Life Planning Community Product'}
          </SheetTitle>
          <SheetDescription>
            Model a Life Planning Community, CCRC, or CCAH Type A Plan. These plans typically cover
            all LTC expenses for a one-time entry and recurring maintenance fee.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-1">
          <FieldRow
            title="Product Name"
            description="The name of the continuing care community Type A plan."
          >
            <div className="space-y-1">
              <Input
                id="ccrc-type-a-product-name-input"
                placeholder="Enter product name"
                value={formState.productName}
                onChange={event => handleNameChange(event.target.value)}
                aria-invalid={!!errors.productName}
              />
              {errors.productName && <p className="text-destructive text-xs">Required field</p>}
            </div>
          </FieldRow>

          <FieldRow
            title="Membership Start Year"
            description="The calendar year when the membership was purchased."
          >
            <IntegerInput
              placeholder="Enter year"
              value={formState.startYear}
              onChange={handleFieldChange('startYear')}
              onPreview={handleFieldChange('startYear')}
              errorMessage={errors.startYear ? 'Required field' : undefined}
            />
          </FieldRow>

          <FieldRow
            title="Monthly Maintenance Fees"
            description="The amount paid monthly to participate in the program."
          >
            <CurrencyInput
              placeholder="Enter dollars"
              value={formState.monthlyFee}
              onChange={handleFieldChange('monthlyFee')}
              onPreview={handleFieldChange('monthlyFee')}
              errorMessage={errors.monthlyFee ? 'Required field' : undefined}
            />
          </FieldRow>

          <FieldRow
            title="Maintenance Fee Increase Rate"
            description="The annual percentage increase in the monthly maintenance fee."
          >
            <PercentInput
              placeholder="Enter %"
              value={formState.annualIncreaseRate}
              onChange={handleFieldChange('annualIncreaseRate')}
              onPreview={handleFieldChange('annualIncreaseRate')}
            />
          </FieldRow>

          <FieldRow
            title="Entrance Fee"
            description="Fee required to enter the program. Provide the total amount, not the installment amount."
          >
            <CurrencyInput
              placeholder="Enter dollars"
              value={formState.entranceFee}
              onChange={handleFieldChange('entranceFee')}
              onPreview={handleFieldChange('entranceFee')}
              errorMessage={errors.entranceFee ? 'Required field' : undefined}
            />
          </FieldRow>
        </div>

        <SheetFooter className="gap-4">
          <Alert variant="info-blue">
            <AlertDescription>{SUBMISSION_DISCLAIMER}</AlertDescription>
          </Alert>
          <Button
            size="lg"
            className="w-full"
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
